// Type-aware rendering of a single submitted answer. Maps each element type to
// how its stored response is displayed (text, option badges, image/file links)
// and how it serializes to a CSV cell, with a text fallback for older/unknown
// data. Used across all the responses views and the CSV export.
import { Badge } from '@/components/ui/badge';
import { Download, FileText } from 'lucide-react';

import {
	SHORT_QUESTION,
	LONG_QUESTION,
	DROPDOWN,
	CHECKBOXES,
	IMAGE,
	FILE,
} from '../questionTypes';

// A stored response object looks like:
//   { text, selectedOptions, attachments: [{ url, name, type, size }], createdAt }
// Older/text-only responses only have `text`. Renderers below read whichever
// shape is present and fall back to text so nothing breaks before the schema
// for the richer types ships.

const getText = (response) =>
	response && typeof response.text === 'string' ? response.text.trim() : '';

const getOptions = (response) =>
	Array.isArray(response?.selectedOptions)
		? response.selectedOptions.filter(Boolean)
		: [];

const getAttachments = (response) =>
	Array.isArray(response?.attachments)
		? response.attachments.filter((a) => a?.url || a?.key)
		: [];

const getAttachmentName = (attachment, index) =>
	attachment?.name || `attachment-${index + 1}`;

function formatBytes(bytes) {
	const n = Number(bytes);
	if (!n || Number.isNaN(n)) return '';
	const units = ['B', 'KB', 'MB', 'GB'];
	let value = n;
	let i = 0;
	while (value >= 1024 && i < units.length - 1) {
		value /= 1024;
		i += 1;
	}
	return `${value.toFixed(value < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

function TextAnswer({ response }) {
	const text = getText(response);
	if (!text) return null;
	return (
		<p className="whitespace-pre-wrap break-words text-sm text-foreground">
			{text}
		</p>
	);
}

function CheckboxesAnswer({ response }) {
	const options = getOptions(response);
	if (options.length === 0) return <TextAnswer response={response} />;
	return (
		<div className="flex flex-wrap gap-1.5">
			{options.map((option) => (
				<Badge key={option} variant="secondary">
					{option}
				</Badge>
			))}
		</div>
	);
}

function ImageAnswer({ response }) {
	const attachments = getAttachments(response);
	if (attachments.length === 0) return <TextAnswer response={response} />;
	return (
		<div className="flex flex-wrap gap-2">
			{attachments.map((attachment) => (
				<a
					key={attachment.key || attachment.url}
					href={attachment.url}
					target="_blank"
					rel="noreferrer"
					className="block h-24 w-24 overflow-hidden rounded-md border border-border"
				>
					<img
						src={attachment.url}
						alt={attachment.name || 'attachment'}
						className="h-full w-full object-cover"
					/>
				</a>
			))}
		</div>
	);
}

function FileAnswer({ response }) {
	const attachments = getAttachments(response);
	if (attachments.length === 0) return <TextAnswer response={response} />;
	return (
		<div className="flex flex-col gap-1.5">
			{attachments.map((attachment) => (
				<a
					key={attachment.key || attachment.url}
					href={attachment.url}
					target="_blank"
					rel="noreferrer"
					className="flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5 text-sm hover:bg-muted"
				>
					<FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
					<span className="min-w-0 flex-1 truncate">
						{attachment.name || attachment.url}
					</span>
					{attachment.size ? (
						<span className="text-xs text-muted-foreground">
							{formatBytes(attachment.size)}
						</span>
					) : null}
					<Download className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
				</a>
			))}
		</div>
	);
}

const textToCsv = (response) => getText(response);

const RENDERERS = {
	[SHORT_QUESTION]: { Render: TextAnswer, toCsv: textToCsv },
	[LONG_QUESTION]: { Render: TextAnswer, toCsv: textToCsv },
	[DROPDOWN]: { Render: TextAnswer, toCsv: textToCsv },
	[CHECKBOXES]: {
		Render: CheckboxesAnswer,
		toCsv: (response) => {
			const options = getOptions(response);
			return options.length ? options.join('; ') : getText(response);
		},
	},
	[IMAGE]: {
		Render: ImageAnswer,
		toCsv: (response) => {
			const attachments = getAttachments(response);
			return attachments.length
				? attachments.map(getAttachmentName).join(' | ')
				: getText(response);
		},
	},
	[FILE]: {
		Render: FileAnswer,
		toCsv: (response) => {
			const attachments = getAttachments(response);
			return attachments.length
				? attachments.map(getAttachmentName).join(' | ')
				: getText(response);
		},
	},
};

const FALLBACK = { Render: TextAnswer, toCsv: textToCsv };

const getRenderer = (type) => RENDERERS[type] || FALLBACK;

export function AnswerView({ type, response }) {
	const { Render } = getRenderer(type);
	return <Render response={response} />;
}

export function answerToCsv(type, response) {
	return getRenderer(type).toCsv(response) || '';
}

export function hasAnswer(type, response) {
	if (!response) return false;
	return Boolean(answerToCsv(type, response).trim());
}
